import React, { useState } from "react";
import { twMerge } from "tailwind-merge";

export const cx = (...a) => a.filter(Boolean).join(" ");

export function Logo({ className = "", dark = false }) {
  return (
    <div className={cx("flex items-center gap-2 font-head font-extrabold tracking-tight", className)}>
      <span className={cx("inline-flex h-7 w-7 items-center justify-center rounded-md text-white", "bg-orange-600")}>
        <i className="fa-solid fa-box-archive text-sm" />
      </span>
      <span className={dark ? "text-white" : "text-slate-900"}>
        DEADSTOCK<span className="text-orange-600">.LIVE</span>
      </span>
    </div>
  );
}

export function Badge({ children, tone = "slate", className = "" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    orange: "bg-orange-600 text-white",
    green: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    emeraldSoft: "bg-emerald-50 text-emerald-700",
  };
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold", tones[tone], className)}>
      {children}
    </span>
  );
}

export function StatusPill({ status }) {
  const map = {
    active: "green", approved: "green", published: "green",
    pending: "amber", under_review: "amber", submitted: "amber",
    inactive: "slate", rejected: "red", expired: "red", private: "slate",
  };
  return <Badge tone={map[status] || "slate"}>{(status || "").replace(/_/g, " ")}</Badge>;
}

export function StatCard({ label, value, icon, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-900", orange: "text-orange-600",
    green: "text-emerald-600", amber: "text-amber-600", blue: "text-blue-600", red: "text-red-600",
  };
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 fade-up">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
        {icon && <i className={cx("fa-solid", icon, "text-slate-300")} />}
      </div>
      <p className={cx("mt-2 font-head text-2xl font-extrabold", tones[tone])}>{value}</p>
    </div>
  );
}

export function Field({ label, children, required, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">
        {label} {required && <span className="text-orange-600">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

export const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition";

export function Input(props) {
  return <input {...props} className={twMerge(inputCls, props.className)} />;
}
export function Select({ children, ...props }) {
  return (
    <select {...props} className={twMerge(inputCls, "appearance-none", props.className)}>
      {children}
    </select>
  );
}

export function Button({ children, variant = "primary", className = "", ...props }) {
  const v = {
    primary: "bg-orange-600 hover:bg-orange-700 text-white",
    dark: "bg-slate-900 hover:bg-slate-800 text-white",
    green: "bg-emerald-600 hover:bg-emerald-700 text-white",
    outline: "border border-slate-300 bg-white hover:bg-slate-50 text-slate-700",
    ghost: "hover:bg-slate-100 text-slate-700",
    danger: "bg-red-600 hover:bg-red-700 text-white",
  };
  return (
    <button
      {...props}
      className={twMerge(
        "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:opacity-50",
        v[variant], className
      )}
    >
      {children}
    </button>
  );
}

export function Empty({ text = "Nothing here yet", icon = "fa-inbox" }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
      <i className={cx("fa-solid", icon, "text-3xl")} />
      <p className="mt-3 text-sm font-medium">{text}</p>
    </div>
  );
}

export function Table({ head, children }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white ds-scroll">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{head.map((h) => <th key={h} className="whitespace-nowrap px-4 py-3 font-bold">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Toolbar({ children }) {
  return <div className="mb-4 flex flex-wrap items-center gap-2">{children}</div>;
}

// Sort helper — fields: [{key,label,type:"date"|"num"|"str",get?}], sort: {field,dir:"asc"|"desc"}
export function sortRows(rows, fields, sort) {
  const meta = fields.find((f) => f.key === sort.field);
  if (!meta) return rows;
  const type = meta.type || "str";
  const dir = sort.dir === "asc" ? 1 : -1;
  const get = meta.get || ((r) => r[sort.field]);
  return [...rows].sort((a, b) => {
    let x = get(a), y = get(b);
    if (type === "date") { x = Date.parse(x) || 0; y = Date.parse(y) || 0; }
    else if (type === "num") { x = Number(x) || 0; y = Number(y) || 0; }
    else { x = (x ?? "").toString().toLowerCase(); y = (y ?? "").toString().toLowerCase(); }
    if (x < y) return -1 * dir;
    if (x > y) return 1 * dir;
    return 0;
  });
}

export function SortControl({ fields, value, onChange, testid = "sort" }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Sort</span>
      <Select className="w-auto" value={value.field} data-testid={`${testid}-field`}
        onChange={(e) => onChange({ ...value, field: e.target.value })}>
        {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
      </Select>
      <button type="button" data-testid={`${testid}-dir`}
        onClick={() => onChange({ ...value, dir: value.dir === "asc" ? "desc" : "asc" })}
        title={value.dir === "asc" ? "Ascending" : "Descending"}
        className="inline-flex h-[38px] items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
        <i className={`fa-solid ${value.dir === "asc" ? "fa-arrow-up-short-wide" : "fa-arrow-down-wide-short"}`} />
        {value.dir === "asc" ? "Asc" : "Desc"}
      </button>
    </div>
  );
}

export function AddableSelect({ value, onChange, options = [], onAdd, placeholder, testid, addTestid, disabled }) {
  const [adding, setAdding] = useState(false);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const name = txt.trim();
    if (!name) return;
    setBusy(true);
    try { await onAdd(name); onChange(name); setTxt(""); setAdding(false); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex gap-1">
        <select className={twMerge(inputCls, "appearance-none")} value={value} disabled={disabled}
          onChange={(e) => onChange(e.target.value)} data-testid={testid}>
          <option value="">{placeholder || "Select"}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <button type="button" onClick={() => setAdding((a) => !a)} disabled={disabled} data-testid={addTestid}
          title="Add new" className="shrink-0 rounded-lg border border-slate-300 px-3 text-sm font-bold text-orange-600 hover:bg-orange-50 disabled:opacity-40">
          <i className="fa-solid fa-plus" />
        </button>
      </div>
      {adding && (
        <div className="mt-1 flex gap-1">
          <input className={inputCls} value={txt} placeholder="Type new value…" autoFocus
            onChange={(e) => setTxt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }} />
          <button type="button" onClick={submit} disabled={busy}
            className="shrink-0 rounded-lg bg-orange-600 px-3 text-sm font-semibold text-white">{busy ? "…" : "Add"}</button>
        </div>
      )}
    </div>
  );
}
